import {Translation} from 'primeng/api';

import {FIRST_DAY_OF_WEEK, SupportedLanguage} from '@mydeals/i18n';

/**
 * PrimeNG's own built-in strings (`p-datepicker` day/month names, nav aria-labels — the
 * only PrimeNG component with user-visible text so far, see `time-input` and the orders
 * `startAt` filter). `PrimeNG.setTranslation()` shallow-merges onto whatever was set
 * before, so every language here must specify the same key set — otherwise a key missing
 * from language B would keep leaking language A's value after a switch.
 *
 * `firstDayOfWeek`: 0 (Sunday) for `en`, 1 (Monday) for `uk`/`ru`/`pl` — the ISO-8601
 * convention those locales expect.
 */
type DatePickerTranslation = Pick<
  Translation,
  | 'dayNames'
  | 'dayNamesShort'
  | 'dayNamesMin'
  | 'monthNames'
  | 'monthNamesShort'
  | 'today'
  | 'clear'
  | 'weekHeader'
  | 'dateFormat'
  | 'firstDayOfWeek'
  | 'chooseYear'
  | 'chooseMonth'
  | 'chooseDate'
  | 'prevDecade'
  | 'nextDecade'
  | 'prevYear'
  | 'nextYear'
  | 'prevMonth'
  | 'nextMonth'
  | 'prevHour'
  | 'nextHour'
  | 'prevMinute'
  | 'nextMinute'
  | 'prevSecond'
  | 'nextSecond'
>;

export const PRIMENG_TRANSLATIONS: Record<SupportedLanguage, DatePickerTranslation> = {
  en: {
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    today: 'Today',
    clear: 'Clear',
    weekHeader: 'Wk',
    dateFormat: 'mm/dd/yy',
    firstDayOfWeek: FIRST_DAY_OF_WEEK.en,
    chooseYear: 'Choose Year',
    chooseMonth: 'Choose Month',
    chooseDate: 'Choose Date',
    prevDecade: 'Previous Decade',
    nextDecade: 'Next Decade',
    prevYear: 'Previous Year',
    nextYear: 'Next Year',
    prevMonth: 'Previous Month',
    nextMonth: 'Next Month',
    prevHour: 'Previous Hour',
    nextHour: 'Next Hour',
    prevMinute: 'Previous Minute',
    nextMinute: 'Next Minute',
    prevSecond: 'Previous Second',
    nextSecond: 'Next Second',
  },
  uk: {
    dayNames: ['неділя', 'понеділок', 'вівторок', 'середа', 'четвер', "п'ятниця", 'субота'],
    dayNamesShort: ['нед', 'пон', 'вів', 'срд', 'чтв', 'птн', 'сбт'],
    dayNamesMin: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
    monthNames: [
      'січень',
      'лютий',
      'березень',
      'квітень',
      'травень',
      'червень',
      'липень',
      'серпень',
      'вересень',
      'жовтень',
      'листопад',
      'грудень',
    ],
    monthNamesShort: ['січ', 'лют', 'бер', 'кві', 'тра', 'чер', 'лип', 'сер', 'вер', 'жов', 'лис', 'гру'],
    today: 'Сьогодні',
    clear: 'Очистити',
    weekHeader: 'Тиж',
    dateFormat: 'dd.mm.yy',
    firstDayOfWeek: FIRST_DAY_OF_WEEK.uk,
    chooseYear: 'Вибрати рік',
    chooseMonth: 'Вибрати місяць',
    chooseDate: 'Вибрати дату',
    prevDecade: 'Попереднє десятиліття',
    nextDecade: 'Наступне десятиліття',
    prevYear: 'Попередній рік',
    nextYear: 'Наступний рік',
    prevMonth: 'Попередній місяць',
    nextMonth: 'Наступний місяць',
    prevHour: 'Попередня година',
    nextHour: 'Наступна година',
    prevMinute: 'Попередня хвилина',
    nextMinute: 'Наступна хвилина',
    prevSecond: 'Попередня секунда',
    nextSecond: 'Наступна секунда',
  },
  ru: {
    dayNames: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'],
    dayNamesShort: ['вск', 'пнд', 'втр', 'срд', 'чтв', 'птн', 'суб'],
    dayNamesMin: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
    monthNames: [
      'январь',
      'февраль',
      'март',
      'апрель',
      'май',
      'июнь',
      'июль',
      'август',
      'сентябрь',
      'октябрь',
      'ноябрь',
      'декабрь',
    ],
    monthNamesShort: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
    today: 'Сегодня',
    clear: 'Очистить',
    weekHeader: 'Нед',
    dateFormat: 'dd.mm.yy',
    firstDayOfWeek: FIRST_DAY_OF_WEEK.ru,
    chooseYear: 'Выбрать год',
    chooseMonth: 'Выбрать месяц',
    chooseDate: 'Выбрать дату',
    prevDecade: 'Предыдущее десятилетие',
    nextDecade: 'Следующее десятилетие',
    prevYear: 'Предыдущий год',
    nextYear: 'Следующий год',
    prevMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',
    prevHour: 'Предыдущий час',
    nextHour: 'Следующий час',
    prevMinute: 'Предыдущая минута',
    nextMinute: 'Следующая минута',
    prevSecond: 'Предыдущая секунда',
    nextSecond: 'Следующая секунда',
  },
  pl: {
    dayNames: ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'],
    dayNamesShort: ['nie', 'pon', 'wto', 'śro', 'czw', 'pią', 'sob'],
    dayNamesMin: ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'],
    monthNames: [
      'styczeń',
      'luty',
      'marzec',
      'kwiecień',
      'maj',
      'czerwiec',
      'lipiec',
      'sierpień',
      'wrzesień',
      'październik',
      'listopad',
      'grudzień',
    ],
    monthNamesShort: ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'],
    today: 'Dzisiaj',
    clear: 'Wyczyść',
    weekHeader: 'Tydz',
    dateFormat: 'dd.mm.yy',
    firstDayOfWeek: FIRST_DAY_OF_WEEK.pl,
    chooseYear: 'Wybierz rok',
    chooseMonth: 'Wybierz miesiąc',
    chooseDate: 'Wybierz datę',
    prevDecade: 'Poprzednia dekada',
    nextDecade: 'Następna dekada',
    prevYear: 'Poprzedni rok',
    nextYear: 'Następny rok',
    prevMonth: 'Poprzedni miesiąc',
    nextMonth: 'Następny miesiąc',
    prevHour: 'Poprzednia godzina',
    nextHour: 'Następna godzina',
    prevMinute: 'Poprzednia minuta',
    nextMinute: 'Następna minuta',
    prevSecond: 'Poprzednia sekunda',
    nextSecond: 'Następna sekunda',
  },
};
